import React, { useMemo } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";

const MUTED = "#64748b";

export default function TrackerQrCodePopup({ open, url, trackerName, onClose }) {
  const { enqueueSnackbar } = useSnackbar();

  const qrSrc = useMemo(() => {
    if (!url) return "";
    return `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=12&data=${encodeURIComponent(
      url
    )}`;
  }, [url]);

  const handleDownload = async () => {
    if (!qrSrc) return;
    try {
      const response = await fetch(qrSrc);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${(trackerName || "tracker")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")}-qr.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      enqueueSnackbar("Could not download QR code", { variant: "error" });
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar("Public link copied", { variant: "success" });
    } catch {
      enqueueSnackbar("Could not copy link", { variant: "error" });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0f172a" }}>
          QR code
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 13, color: MUTED }}>
          Scan to open the public join link.
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            py: 1,
          }}
        >
          {trackerName ? (
            <Typography
              sx={{
                fontSize: 17,
                fontWeight: 700,
                color: "#0f172a",
                textAlign: "center",
                lineHeight: 1.3,
                maxWidth: 320,
                wordBreak: "break-word",
              }}
            >
              {trackerName}
            </Typography>
          ) : null}
          {qrSrc ? (
            <Box
              component="img"
              src={qrSrc}
              alt="Tracker public link QR code"
              sx={{
                width: 240,
                height: 240,
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                bgcolor: "#fff",
                p: 1,
              }}
            />
          ) : (
            <Typography sx={{ color: MUTED, fontSize: 14 }}>
              No public link available.
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: 12,
              color: MUTED,
              textAlign: "center",
              wordBreak: "break-all",
              maxWidth: 320,
            }}
          >
            {url}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: "wrap" }}>
        <button
          type="button"
          className="small-contained-outlined-button"
          onClick={handleCopyLink}
        >
          Copy link
        </button>
        <button
          type="button"
          className="small-contained-outlined-button"
          onClick={handleDownload}
          disabled={!qrSrc}
        >
          Download
        </button>
        <button
          type="button"
          className="small-contained-button"
          onClick={onClose}
        >
          Done
        </button>
      </DialogActions>
    </Dialog>
  );
}
